/** @file Dialog to add a single unit */
import { Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import * as React from 'react';

import { SWLoadingButton } from '@/components/LoadingButton';

import type { PipeDiameter, PipeMaterial } from '../types';

type Props = {
  name: string;
  pipeDiameter: PipeDiameter;
  pipeMaterial: PipeMaterial;
  open: boolean;
  onClose: () => void;
  addOrEdit: 'ADD' | 'EDIT';
  type: 'UNIT' | 'COMMON_AREA';
  onSubmit: (data: {
    name: string;
    pipeDiameter: PipeDiameter;
    pipeMaterial: PipeMaterial;
    unitFlag?: 'NON_BILLABLE' | 'COMMON_AREA' | 'SPLIT'[];
  }) => void;
  loading: boolean;
};

/**
 * Create Unit dialog
 *
 * @param {object} props - Component properties
 * @returns {React.Component} - Create Unit dialog
 */
export const FormDialog = (props: Props) => {
  const [localName, setLocalName] = React.useState(props.name);
  const [unitFlag, setUnitFlag] = React.useState<'NON_BILLABLE' | 'COMMON_AREA' | 'SPLIT'>(
    'NON_BILLABLE',
  );
  const [openSplitWarning, setOpenSplitWarning] = React.useState(false);
  // TODO: backend OpenAPI does not enforce non-empty. Add `minLength: 1` to `Unit.name`
  // in customer-lambdas/doc/webapp.yaml — DB column is NOT NULL but accepts empty
  // strings, so without this client-side guard a user can save a blank unit name.
  const isNameBlank = (localName ?? '').trim() === '';

  const handleSubmit = () => {
    if (isNameBlank) return;
    const trimmedName = (localName ?? '').trim();
    let body = {
      name: trimmedName,
      pipeDiameter: props.pipeDiameter,
      pipeMaterial: props.pipeMaterial,
    };
    if (props.type === 'COMMON_AREA') body['unitFlag'] = ['COMMON_AREA', unitFlag];

    if (unitFlag === 'SPLIT') {
      setOpenSplitWarning(true);
    } else {
      props.onSubmit(body);
    }
  };

  const handleConfirmSplit = () => {
    setOpenSplitWarning(false);
    const trimmedName = (localName ?? '').trim();
    if (trimmedName === '') return;

    let body = {
      name: trimmedName,
      pipeDiameter: props.pipeDiameter,
      pipeMaterial: props.pipeMaterial,
    };
    if (props.type === 'COMMON_AREA') body['unitFlag'] = ['COMMON_AREA', unitFlag];
    props.onSubmit(body);
  };

  return (
    <>
      <Dialog open={props.open} onClose={props.onClose}>
        <DialogTitle>{`${props.addOrEdit === 'ADD' ? 'Add' : 'Edit'} ${
          props.type === 'UNIT' ? 'Unit' : 'Common Area'
        }`}</DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            {props.name !== undefined && (
              <TextField
                label={'Name'}
                placeholder={'Name'}
                error={isNameBlank}
                helperText={
                  isNameBlank
                    ? 'Name is required'
                    : `eg: "1", "205-b"  (${(localName ?? '').length}/64)`
                }
                required={true}
                value={localName}
                sx={{ marginTop: 2 }}
                // TODO: backend OpenAPI does not enforce this length. Add `maxLength: 64`
                // to `Unit.name` in customer-lambdas/doc/webapp.yaml (mirroring units.name
                // varchar(64) in db_setup.sql), then delete this client-side cap + counter.
                // Today the DB write fails with a cryptic string_data_right_truncation
                // error if the cap is bypassed. The bulk-import paths
                // (Units/FormDialog/bulk.tsx, Structures/Create/CreateUnits/index.tsx)
                // also bypass this client-side cap, so the backend fix is the durable one.
                inputProps={{ maxLength: 64 }}
                onChange={(e) => {
                  setLocalName(e.target.value);
                }}
              />
            )}
            {props.type === 'COMMON_AREA' && (
              <FormControl fullWidth>
                <InputLabel id="unitflag">Bill Model</InputLabel>
                <Select
                  labelId="unitflag"
                  value={unitFlag}
                  label="Bill Model"
                  onChange={(e) =>
                    setUnitFlag(e.target.value as 'NON_BILLABLE' | 'COMMON_AREA' | 'SPLIT')
                  }
                >
                  <MenuItem value={'NON_BILLABLE'}>Non Billable</MenuItem>
                  <MenuItem value={'SPLIT'}>Split</MenuItem>
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <SWLoadingButton onClick={handleSubmit} loading={props.loading} disabled={isNameBlank}>
            {props.addOrEdit === 'ADD' ? 'Add' : 'Save'}
          </SWLoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openSplitWarning} onClose={() => setOpenSplitWarning(false)}>
        <DialogTitle>Warning: Split Billing</DialogTitle>
        <DialogContent>
          This is not recommended for residential properties and can be prohibited. Please confirm
          this is a commercial property where you'd like to split the common water costs among
          tenants.
        </DialogContent>
        <DialogActions>
          <Button variant={'outlined'} onClick={() => setOpenSplitWarning(false)}>
            Cancel
          </Button>
          <SWLoadingButton onClick={handleConfirmSplit}>Confirm</SWLoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
